import { useEffect, useRef } from "react"
import { useStore } from "react-redux"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useStalenessTick } from "@/hooks/useStalenessTick"
import { computeGpsIssues, STALE_THRESHOLD_MS } from "@/lib/gpsIssues"
import { getReconnectsLastHour } from "@/lib/xmppReconnectTracker"
import {
  getLocationSubscriptionState,
  type LocationSubscriptionState,
} from "@/lib/locationSubscriptionTracker"
import {
  notifyDebugSubscribers,
  updateGpsStaleByChat,
} from "@/lib/slickshiftDebug"
import { isGpsConfigured } from "@/reducers/gpsConfigSelectors"
import { isDriverRoomDataReady } from "@/reducers/roomSyncStatusSelectors"
import type { RootState } from "@/reducers/store"

type GpsBecameStalePayload = {
  chatJid: string
  routeJid: string
  chatGroup: "driver" | "shared_driver"
  lastLocationReceivedAt: number
  msSinceLastLocation: number
  xmppConnectionStatus: "online" | "disconnected" | "offline"
  browserOnline: boolean
  xmppReconnectsLastHour: number
  locationSubscription: LocationSubscriptionState
  staleThresholdMs: number
}

export const useStaleGpsWatcher = (): void => {
  const store = useStore<RootState>()
  const now = useStalenessTick()
  const prevStaleByChat = useRef<Map<string, boolean>>(new Map())
  // Gated rollout: PostHog flag controls who emits the Segment event. Watcher
  // state (per-chat stale tracking, debug snapshot) keeps running for everyone
  // so the dev-console / extension introspection works regardless of the flag.
  // Default-off semantics: undefined while PostHog loads → no events.
  const eventsEnabled = useFeatureFlagEnabled("gps-stale-analytics")

  useEffect(() => {
    const state = store.getState()
    if (!isDriverRoomDataReady(state)) return

    // Monitor both original "driver" rooms and "shared_driver" rooms in the
    // user's chat list. Shared rooms receive their own geolocation pushes under
    // a distinct JID, so a shared room going stale is a separate signal worth
    // reporting. The `chatGroup` field on the payload lets consumers (analytics
    // dashboards, CloudWatch metric dimensions) split the two if needed.
    const currentChatJids = new Set<string>()

    for (const chat of state.chats) {
      const chatGroup = state.accountChats[chat.jid]?.group
      if (chatGroup !== "driver" && chatGroup !== "shared_driver") continue
      const chatJid = chat.jid
      currentChatJids.add(chatJid)

      const lastReceivedAt = state.locations[chatJid]?.receivedAt

      const { gpsStale } = computeGpsIssues({
        isGpsConfigured: isGpsConfigured(state.gpsConfigs[chatJid]),
        lastReceivedAt,
        now,
      })

      const prev = prevStaleByChat.current.get(chatJid)
      prevStaleByChat.current.set(chatJid, gpsStale)

      // Baseline: never fire on first observation.
      if (prev === undefined) continue

      // Only fire on transition false → true.
      if (prev === false && gpsStale === true) {
        // lastReceivedAt must be defined here — gpsStale requires it.
        const payload: GpsBecameStalePayload = {
          chatJid,
          routeJid: chatJid,
          chatGroup,
          lastLocationReceivedAt: lastReceivedAt as number,
          msSinceLastLocation: now - (lastReceivedAt as number),
          xmppConnectionStatus: state.connectionStatus.value,
          browserOnline: navigator.onLine,
          xmppReconnectsLastHour: getReconnectsLastHour(now),
          locationSubscription: getLocationSubscriptionState(now),
          staleThresholdMs: STALE_THRESHOLD_MS,
        }
        if (eventsEnabled) {
          window.analytics.track("GpsBecameStale", payload)
        }
      }
    }

    // Prune entries for chats that disappeared from the driver list.
    for (const tracked of prevStaleByChat.current.keys()) {
      if (!currentChatJids.has(tracked)) {
        prevStaleByChat.current.delete(tracked)
      }
    }

    // Publish current per-chat stale state to the debug broker + notify any
    // dev-console / extension subscribers. Object.fromEntries keeps the snapshot
    // serialisable so it can cross the postMessage boundary into the extension.
    updateGpsStaleByChat(Object.fromEntries(prevStaleByChat.current))
    notifyDebugSubscribers()
  }, [now, store, eventsEnabled])
}
